import { useState, useEffect } from 'react';
import { getCurrencySymbol } from '../utils/currency';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { readGlobalProfiles } from '@services/firebaseGlobal';
import { getFirebase } from '@services/firebase';
import './css/OrderHistory.css';
import ImageZoomModal from './../components/modals/ImageZoomModal';

const OrderHistory = ({ userRole: propUserRole }) => {
  const { db, auth } = getFirebase();
  const [user, loading, error] = useAuthState(auth);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [groupedOrders, setGroupedOrders] = useState({});
  const [userRole, setUserRole] = useState(() => {
    if (propUserRole) return propUserRole;
    try {
      const cachedRole = localStorage.getItem('userRole');
      return cachedRole || '';
    } catch (err) {
      console.error('Error reading userRole from localStorage:', err);
      return '';
    }
  });
  const [fetchError, setFetchError] = useState(null);
  const [expandedYears, setExpandedYears] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [tierModal, setTierModal] = useState(null);

  const cleanQuantity = (quantityStr) => {
    if (quantityStr == null || quantityStr === 'N/A') return '';
    const s = String(quantityStr);
    const cleaned = s.replace(/\s*\([^)]*\)/g, '').trim();
    return cleaned || s;
  };

  // Normalise a value that may be a Firestore Timestamp, JS Date, or string
  const toDateString = (val) => {
    if (!val) return null;
    // Firestore Timestamp
    if (typeof val === 'object' && typeof val.toDate === 'function') {
      return val.toDate().toISOString().split('T')[0]; // YYYY-MM-DD
    }
    // JS Date
    if (val instanceof Date) return val.toISOString().split('T')[0];
    return String(val);
  };

  // Function to convert DD.MM.YYYY to proper date format
  const convertDateFormat = (dateString) => {
    if (!dateString || dateString === 'N/A') return null;
    
    // Handle DD.MM.YYYY format
    const match = dateString.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (match) {
      const [, day, month, year] = match;
      // Convert to YYYY-MM-DD format for proper Date parsing
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    return dateString;
  };

  const handleClose = () => {
    navigate(-1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Handle tier click
  const handleTierClick = (order) => {
    setTierModal(order);
    document.body.style.overflow = 'hidden';
  };

  // Close tier modal
  const closeTierModal = () => {
    setTierModal(null);
    document.body.style.overflow = 'auto';
  };

  const toggleYearGroup = (year) => {
    setExpandedYears(prev => {
      const newSet = new Set(prev);
      if (newSet.has(year)) {
        newSet.delete(year);
      } else {
        newSet.add(year);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const normalised = toDateString(dateString);
    if (!normalised) return 'N/A';
    try {
      const converted = convertDateFormat(normalised);
      const date = new Date((converted || normalised) + (converted?.match(/^\d{4}-\d{2}-\d{2}$/) ? 'T00:00:00' : ''));
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch (error) {
      return 'N/A';
    }
  };

  // Improved image rendering in the table
  const renderOrderImage = (order) => {
    if (!order.images || !order.images[0]) {
      return <div className="no-image-placeholder">No Image</div>;
    }

    const imageUrl = order.images[0];

    return (
      <img
        src={imageUrl}
        alt="Order"
        className="order-image"
        loading="lazy"
        style={{ cursor: "zoom-in" }}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedImageUrl(imageUrl);
          setZoomModalOpen(true);
        }}
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    );
  };

  // Fetch user role
  useEffect(() => {
    if (!user) return;
    const fetchUserRole = async () => {
      try {
        const userList = await readGlobalProfiles('cUsers');
        const currentUserData = userList.find(userData => userData.id === user.uid);
        if (currentUserData) {
          let role = 'Buyer';
          if (currentUserData.isbuyer) role = 'Buyer';
          setUserRole(role);
          try { localStorage.setItem('userRole', role); } catch (_) {}
        } else {
          setUserRole('Buyer');
        }
      } catch (err) {
        console.error('Error fetching user role:', err);
        setUserRole('Buyer');
      }
    };
    if (!propUserRole) fetchUserRole();
  }, [user, propUserRole]);

  // Real-time snapshot for oldOrder (Buyer only)
  useEffect(() => {
    if (!user || !db || !userRole) return;
    const q = query(collection(db, 'oldOrder'), where('userId', '==', user.uid));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setFetchError(null);
        const allOrders = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }));
        setOrders(allOrders);
      },
      (err) => {
        console.error('Error listening to oldOrder:', err);
        setFetchError(`Failed to load orders: ${err.message}`);
        setOrders([]);
      }
    );
    return () => unsubscribe();
  }, [user, userRole]);

  const applyFiltersAndSort = () => {
    let filtered = [...orders];

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(order =>
        Object.values(order).some(value => {
          if (Array.isArray(value)) {
            return value.some(v => {
              if (v && typeof v === 'object' && !Array.isArray(v)) {
                return Object.values(v).some(subVal => subVal && String(subVal).toLowerCase().includes(q));
              }
              return v && String(v).toLowerCase().includes(q);
            });
          } else if (value && typeof value === 'object') {
            return Object.values(value).some(subVal => subVal && String(subVal).toLowerCase().includes(q));
          }
          return value !== undefined && value !== null && String(value).toLowerCase().includes(q);
        })
      );
    }

    // Normalise deliveryDate on each order to a plain string
    filtered = filtered.map(o => ({
      ...o,
      _dateStr: toDateString(o.deliveryDate) || toDateString(o.created)
    }));

    // === SORT BY DELIVERY DATE DESCENDING (MAIN SORT) ===
    filtered.sort((a, b) => {
      const dateA = a._dateStr ? new Date(convertDateFormat(a._dateStr)) : new Date(0);
      const dateB = b._dateStr ? new Date(convertDateFormat(b._dateStr)) : new Date(0);
      return dateB - dateA;
    });

    // === GROUP BY YEAR ===
    const groupedByYear = {};

    filtered.forEach(order => {
      const dateStr = order._dateStr;
      let year = 'Unknown';
      if (dateStr) {
        const date = new Date(convertDateFormat(dateStr));
        if (!isNaN(date.getTime())) year = date.getFullYear().toString();
      }
      if (!groupedByYear[year]) groupedByYear[year] = [];
      groupedByYear[year].push(order);
    });

    // Sort years descending (Unknown goes last)
    const sortedYears = Object.keys(groupedByYear).sort((a, b) => {
      if (a === 'Unknown') return 1;
      if (b === 'Unknown') return -1;
      return parseInt(b) - parseInt(a);
    });

    const finalGrouped = {};
    sortedYears.forEach(year => { finalGrouped[year] = groupedByYear[year]; });

    setGroupedOrders(finalGrouped);
    setFilteredOrders(filtered);

    // Auto-expand the most recent year
    if (sortedYears.length > 0) {
      setExpandedYears(new Set([sortedYears[0]]));
    }
  };

  useEffect(() => {
    applyFiltersAndSort();
  }, [orders, searchQuery]);


  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;
  if (!user) return <div>Please log in to access this page.</div>;
  if (userRole !== 'Buyer') return null;

  const years = Object.keys(groupedOrders);

  return (
    <div className="profile-page">
      <div className="page-content">
        <button className="page-close-btn" onClick={handleClose}>
          <FaTimes />
        </button>
        <div className="page-body">
          {tierModal && (
            <div 
              className="customer-modal-overlay"
              onClick={closeTierModal}
            >
              <div 
                className="customer-modal-content"
                onClick={e => e.stopPropagation()}
              >
                <button 
                  className="customer-modal-close-btn"
                  onClick={closeTierModal}
                  title="Close"
                >
                  ×
                </button>
                <div className="customer-modal-details">
                  {[0, 1, 2]
                    .filter(i => i === 0 || (tierModal.tierCount || tierModal.tiers?.length || 1) > i)
                    .map(i => {
                      const t = tierModal.tiers?.[i] || {};
                      return (
                        <div key={i}>
                          <div className="customer-detail-row">
                            <span className="customer-detail-label" style={{fontWeight: 'bold', color: 'var(--text-primary)'}}>
                              Tier {i + 1} :
                            </span>
                            <span className="customer-detail-value"></span>
                          </div>
                          <div className="customer-detail-row">
                            <span className="customer-detail-label">Sponge</span>
                            <span className="customer-detail-value">{t.sponge || tierModal.sponge || 'N/A'}</span>
                          </div>
                          <div className="customer-detail-row">
                            <span className="customer-detail-label">Flavour</span>
                            <span className="customer-detail-value">{t.cflavour || 'N/A'}</span>
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              </div>
            </div>
          )}
          {fetchError && <p className="error-message">{fetchError}</p>}
          <div className="oh-search-wrapper">
            <input
              type="text"
              className="oh-search-input"
              placeholder="Search orders..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <button className="oh-search-clear-btn" onClick={handleClearSearch}>
                <FaTimes />
              </button>
            )}
          </div>
          <div className="tables-scroll-container">
            {Object.keys(groupedOrders)
              .sort((a, b) => parseInt(b) - parseInt(a))
              .map((year, yearIndex) => {
                const ordersForYear = groupedOrders[year];

                return (
                  <div key={year}>
                    {/* YEAR HEADER */}
                    <div className="table-header">
                      <h3 style={{ margin: 0 }}>{year}</h3>
                      <button
                        className="toggle-btn"
                        onClick={() => toggleYearGroup(year)}
                      >
                        {expandedYears.has(year) ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                    </div>
                      {expandedYears.has(year) && (
                        <div style={{ marginTop: '8px' }}>
                          <table className="order-table">
                            <thead>
                              <tr>
                                <th>Image</th>
                                <th>Date</th>
                                <th>Status</th>
                                <th>Flavour</th>
                                <th>Sponge Type</th>
                                <th>Cover</th>
                                <th>Tier</th>
                                <th>Quantity</th>
                                <th>Cost</th>
                                <th>Delivery</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ordersForYear.map((order) => (
                                <tr key={order.id || order.globalId}>
                                  <td>{renderOrderImage(order)}</td>
                                  <td>{formatDate(order.deliveryDate)}</td>
                                  <td style={{
                                    fontWeight: 600,
                                    color: order.reqState === 'Order Cancelled' ? '#fa4848' : '#2e7d32'
                                  }}>
                                    {order.reqState === 'Order Cancelled'
                                      ? 'Cancelled'
                                      : order.del_mode === false
                                        ? 'Picked Up'
                                        : 'Delivered'}
                                  </td>
                                  <td>{order.tiers?.[0]?.cflavour || ''}</td>
                                  <td>{order.eggless ? 'Eggless' : 'With Egg'}</td>
                                  <td>{order.cover || ''}</td>
                                  <td 
                                    className="tier-cell"
                                    style={{color: '#fa4848', fontWeight: 'bold', textAlign: 'center'}}
                                    onClick={() => handleTierClick(order)}
                                  >
                                    {order.tierCount || order.tiers?.length || ''}
                                  </td>
                                  <td>{cleanQuantity(order.quantity) ? `${cleanQuantity(order.quantity)} Kilogram` : ''}</td>
                                  <td>{getCurrencySymbol() + ((parseFloat(order.ccost) || 0) + (parseFloat(order.del_cost) || 0)).toFixed(2)}</td>
                                  <td>{order.del_mode ? 'Yes' : 'No'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                  </div>
                );
              })}

            {Object.keys(groupedOrders).length === 0 && (
              <p className="no-match-message">No orders found.</p>
            )}
          </div>
        </div>
      </div>
      <ImageZoomModal
        isOpen={zoomModalOpen}
        onRequestClose={() => setZoomModalOpen(false)}
        imageUrl={selectedImageUrl}
      />
    </div>
  );
};

export default OrderHistory;